import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Sprout, Globe, LogIn, LogOut, Menu, X, User as UserIcon } from "lucide-react";

const Header = () => {
  const { lang, toggle, t } = useLang();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/mandi", label: t.nav.mandi, testid: "nav-mandi" },
    { to: "/terms", label: t.nav.terms, testid: "nav-terms" },
    { to: "/privacy", label: t.nav.privacy, testid: "nav-privacy" },
    { to: "/legal", label: t.nav.legal_info, testid: "nav-legal" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-stone-200"
    >
      <div className="container-gc px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        {/* Brand */}
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group flex-shrink-0">
          <div
            className="w-10 h-10 rounded-xl bg-white border border-stone-200 flex-shrink-0 shadow-sm"
            style={{
              backgroundImage: "url('https://customer-assets-m6fa6gv7.emergentagent.net/job_site-framework-8/artifacts/u4lqn226_1780408844558.png')",
              backgroundSize: "170% auto",
              backgroundPosition: "center 22%",
              backgroundRepeat: "no-repeat",
            }}
            aria-label="Garlic Capital"
          />
          <div className="leading-tight">
            <div className="font-bold text-stone-900 text-base sm:text-lg">{t.brand}</div>
            <div className="text-[10px] sm:text-xs text-stone-500 -mt-0.5">{t.brand_sub}</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav data-testid="desktop-nav" className="hidden xl:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(item.to)
                  ? "bg-[#1E4D2B] text-white"
                  : "text-stone-700 hover:bg-stone-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            data-testid="language-toggle-btn"
            onClick={toggle}
            className="btn-ghost !h-10 !px-3 text-sm"
            aria-label="Toggle language"
          >
            <Globe size={16} />
            <span className="font-semibold">{lang === "en" ? "हिं" : "EN"}</span>
          </button>

          {/* Auth (desktop) */}
          <div className="hidden xl:flex items-center gap-2">
            {user ? (
              <>
                <Link to="/profile" data-testid="profile-link" className="btn-ghost !h-10 !px-3 text-sm">
                  {user.profile_picture ? (
                    <img src={user.profile_picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <UserIcon size={16} />
                  )}
                  <span className="max-w-[100px] truncate">{user.full_name || user.email}</span>
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/"); }}
                  className="btn-ghost !h-10 !px-3 text-sm"
                  aria-label="Logout"
                >
                  <LogOut size={16} />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="login-link" className="btn-primary !h-10 !px-5 text-sm">
                <LogIn size={16} />
                <span>{t.nav.login}</span>
              </Link>
            )}
          </div>

          {/* Hamburger (mobile/tablet) */}
          <button
            data-testid="hamburger-btn"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={open}
            className="xl:hidden w-10 h-10 rounded-xl border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-stone-100"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="xl:hidden fixed inset-0 top-16 bg-black/30 backdrop-blur-sm z-40"
            onClick={close}
            data-testid="mobile-menu-backdrop"
          />
          <div
            data-testid="mobile-menu"
            className="xl:hidden absolute top-16 inset-x-0 z-50 bg-white border-b border-stone-200 shadow-lg animate-fade-up max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <nav className="container-gc px-4 py-3 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`m-${item.testid}`}
                  onClick={close}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-[#1E4D2B] text-white"
                      : "text-stone-700 hover:bg-stone-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="h-px bg-stone-200 my-2" />
              {user ? (
                <>
                  <Link
                    to="/profile"
                    data-testid="m-profile-link"
                    onClick={close}
                    className="px-4 py-3 rounded-xl text-base font-medium text-stone-700 hover:bg-stone-100 flex items-center gap-3"
                  >
                    {user.profile_picture ? (
                      <img src={user.profile_picture} alt="" className="w-7 h-7 rounded-full" />
                    ) : (
                      <UserIcon size={18} />
                    )}
                    {t.nav.profile}
                  </Link>
                  <button
                    data-testid="m-logout-btn"
                    onClick={() => { close(); logout(); navigate("/"); }}
                    className="px-4 py-3 rounded-xl text-base font-medium text-stone-700 hover:bg-stone-100 text-left flex items-center gap-3"
                  >
                    <LogOut size={18} /> {t.nav.logout}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="m-login-link"
                  onClick={close}
                  className="btn-primary !h-12 mt-1"
                >
                  <LogIn size={18} />
                  <span>{t.nav.login}</span>
                </Link>
              )}
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
